import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/app-shell";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useSubscription } from "@/lib/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useProfile } from "@/lib/useProfile";
import { track } from "@/lib/analytics";

export const Route = createFileRoute("/plans")({
  head: () => ({
    meta: [
      { title: "Plans — Cailève" },
      {
        name: "description",
        content: "Choose the Cailève plan that fits how you style.",
      },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): { success?: boolean; tier?: "premium" | "atelier" } => ({
    success: search.success === "true" || search.success === true,
    tier:
      typeof search.tier === "string" &&
      (search.tier === "premium" || search.tier === "atelier")
        ? (search.tier as "premium" | "atelier")
        : undefined,
  }),
  component: () => (
    <AppShell>
      <PlansPage />
    </AppShell>
  ),
});

const SERIF = "'Cormorant Garamond', Georgia, serif";
const BG = "var(--caileve-bg)";
const INK = "var(--caileve-ink)";
const MUTED = "var(--caileve-muted)";
const LILAC = "var(--caileve-lilac)";
const LILAC_BG = "var(--caileve-soft)";
const GOLD = "var(--caileve-gold)";

function PlansPage() {
  const { tier, isLoading } = useSubscription();
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const hasStripeSub = !!(profile as { stripe_subscription_id?: string | null } | null | undefined)
    ?.stripe_subscription_id;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ from: "/plans" });
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [loadingTier, setLoadingTier] = useState<
    "premium" | "atelier" | null
  >(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [billing, setBilling] = useState<"monthly" | "annual">("monthly");
  const isAnnual = billing === "annual";

  async function openPortal() {
    setPortalBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "customer-portal",
        { body: { return_url: `${window.location.origin}/plans` } },
      );
      if (error) throw new Error(error.message || "Could not open portal");
      const url = (data as { url?: string } | null)?.url;
      if (!url) throw new Error("Portal URL missing");
      window.location.href = url;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not open portal");
      setPortalBusy(false);
    }
  }

  const isFree = !isLoading && tier === "free";
  const isPremium = !isLoading && tier === "premium";
  const isAtelier = !isLoading && tier === "atelier";

  const { data: foundingCount } = useQuery({
    queryKey: ["founding_member_count"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_founding_member_count");
      if (error) throw new Error(error.message || "Network error");
      return (data as number) ?? 0;
    },
    refetchInterval: 30_000,
    staleTime: 30 * 1000,
  });

  const foundingSpotsRemaining = Math.max(0, 100 - (foundingCount ?? 0));
  const foundingAvailable = foundingSpotsRemaining > 0;

  // Price IDs (test mode). Picks founding price when spots remain, else regular monthly.
  const PRICE_IDS = {
    premium: {
      founding: "price_1TceDUFRmUOHvZZvmxJY7Gcq",
      monthly: "price_1TceEZFRmUOHvZZvtctKLzpM",
      annual: "price_1TceFZFRmUOHvZZv79qc1yo4",
    },
    atelier: {
      founding: "price_1TceGTFRmUOHvZZvGWJMdDcC",
      monthly: "price_1TceHTFRmUOHvZZvkUw08ZaC",
      annual: "price_1TceIRFRmUOHvZZvg6Tq2aB1",
    },
  } as const;

  async function handleUpgrade(targetTier: "premium" | "atelier") {
    if (!user) {
      toast.error("Please sign in to upgrade.");
      return;
    }
    setLoadingTier(targetTier);
    try {
      const priceId = isAnnual
        ? PRICE_IDS[targetTier].annual
        : foundingAvailable
          ? PRICE_IDS[targetTier].founding
          : PRICE_IDS[targetTier].monthly;

      const successUrl = `${window.location.origin}/plans?success=true&tier=${targetTier}`;
      const cancelUrl = `${window.location.origin}/plans`;

      track("upgrade_initiated", {
        eye_verdicts_before_upgrade:
          (profile as { eye_verdict_count?: number } | null)?.eye_verdict_count ?? 0,
        tier: targetTier,
        billing: isAnnual ? "annual" : foundingAvailable ? "founding" : "monthly",
      });

      const { data, error } = await supabase.functions.invoke(
        "create-checkout-session",
        {
          body: {
            price_id: priceId,
            success_url: successUrl,
            cancel_url: cancelUrl,
          },
        },
      );

      if (error) throw new Error(error.message || "Checkout failed");
      const url = (data as { url?: string } | null)?.url;
      if (!url) throw new Error("Checkout URL missing in response");

      window.location.href = url;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not start checkout";
      toast.error(msg);
      setLoadingTier(null);
    }
  }

  // Handle return from Stripe Checkout: show toast + refresh subscription/founding count
  useEffect(() => {
    if (!search.success) return;
    const successfulTier = search.tier;
    const tierName =
      successfulTier === "atelier"
        ? "Atelier"
        : successfulTier === "premium"
          ? "Premium"
          : "your new plan";

    track("upgrade_completed", {
      tier: successfulTier ?? "unknown",
      eye_verdicts_before_upgrade:
        (profile as { eye_verdict_count?: number } | null)?.eye_verdict_count ?? 0,
    });

    toast.success(
      `Welcome to ${tierName}. Your collection just got a lot more powerful.`,
      {
        duration: 6000,
        style: {
          fontFamily: SERIF,
          color: LILAC,
          textAlign: "center",
          background: BG,
          border: `1px solid ${LILAC}`,
          fontSize: "15px",
        },
      },
    );

    // Refresh subscription tier + founding count (webhook may take a moment)
    const refresh = () => {
      queryClient.invalidateQueries({ queryKey: ["subscription"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      queryClient.invalidateQueries({ queryKey: ["founding_member_count"] });
    };
    refresh();
    const t1 = setTimeout(refresh, 2000);
    const t2 = setTimeout(refresh, 5000);

    // Strip query params so the toast doesn't re-fire on re-render
    navigate({
      to: "/plans",
      search: { success: false, tier: undefined },
      replace: true,
    });

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.success]);

  return (
    <div style={{ backgroundColor: BG }} className="min-h-screen pb-32">
      <div className="max-w-6xl mx-auto px-6 pt-16 pb-12 text-center">
        <h1
          className="text-4xl md:text-5xl uppercase"
          style={{
            fontFamily: SERIF,
            color: INK,
            letterSpacing: "4px",
          }}
        >
          CAILÈVE
        </h1>
        <p
          className="italic text-2xl mt-4"
          style={{ fontFamily: SERIF, color: MUTED }}
        >
          The women who wear their collections best know exactly what they own — and exactly which pieces belong with them.
        </p>
        {(() => {
          const claimed = foundingCount ?? 0;
          const remaining = Math.max(0, 100 - claimed);
          const closed = remaining <= 0;
          return (
            <>
              <p
                className="italic text-lg mt-6"
                style={{ fontFamily: SERIF, color: GOLD }}
              >
                {closed
                  ? "Founding member pricing is now closed"
                  : `${remaining} founding member spots remaining`}
              </p>
              {!closed && (
                <p
                  className="italic text-sm mt-1"
                  style={{ fontFamily: SERIF, color: MUTED }}
                >
                  Founding Members lock in their price forever.
                </p>
              )}
            </>
          );
        })()}
      </div>

      {/* Billing period toggle */}
      <div className="max-w-6xl mx-auto px-6 pb-8 flex flex-col items-center">
        <div
          role="tablist"
          aria-label="Billing period"
          className="w-full flex"
          style={{
            maxWidth: 280,
            backgroundColor: LILAC_BG,
            borderRadius: 8,
            padding: 4,
          }}
        >
          {(["monthly", "annual"] as const).map((opt) => {
            const active = billing === opt;
            return (
              <button
                key={opt}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setBilling(opt)}
                className="flex-1 py-2 text-xs uppercase tracking-[0.2em] transition-colors"
                style={{
                  backgroundColor: active ? LILAC : "transparent",
                  color: active ? BG : LILAC,
                  borderRadius: 6,
                  fontFamily: SERIF,
                  letterSpacing: "0.25em",
                }}
              >
                {opt === "monthly" ? "Monthly" : "Annual"}
              </button>
            );
          })}
        </div>
        {isAnnual && (
          <p
            className="mt-3 text-xs italic"
            style={{ color: GOLD, fontFamily: SERIF }}
          >
            Save up to 33% with annual billing
          </p>
        )}
      </div>

      <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Free */}
        <article
          className="p-8 flex flex-col relative"
          style={{
            backgroundColor: BG,
            border: `1px solid ${GOLD}`,
            borderTop: isFree ? `1px solid ${GOLD}` : `1px solid ${GOLD}`,
          }}
        >
          {isFree && (
            <Check
              size={14}
              className="absolute top-3 right-3"
              style={{ color: GOLD }}
            />
          )}
          <h2 className="text-3xl" style={{ fontFamily: SERIF, color: INK }}>
            Free
          </h2>
          <p
            className="mt-3 text-3xl"
            style={{ fontFamily: SERIF, color: INK }}
          >
            $0
          </p>
          <ul
            className="mt-6 space-y-2 text-sm leading-relaxed flex-1"
            style={{ color: INK }}
          >
            <li>Up to 20 jewelry pieces</li>
            <li>Up to 20 clothing items</li>
            <li>7 saved looks</li>
            <li>Unlimited accessories</li>
            <li>Style Me — editorial concierge</li>
            <li>Style Me — up to 10 looks per day</li>
            <li>3 Cailève Eye scans per month</li>
          </ul>
          {isFree && (
            <p
              className="mt-8 text-center"
              style={{
                fontFamily: SERIF,
                fontStyle: "italic",
                fontSize: 13,
                color: "var(--caileve-gold-text)",
                letterSpacing: "0.05em",
              }}
            >
              ✦ Your current plan
            </p>
          )}
        </article>

        {/* Premium */}
        <article
          className="p-8 flex flex-col relative"
          style={{
            backgroundColor: LILAC_BG,
            border: `1px solid ${LILAC}`,
            borderTop: isPremium ? `1px solid ${GOLD}` : `1px solid ${LILAC}`,
          }}
        >
          {isPremium && (
            <Check
              size={14}
              className="absolute top-3 right-3"
              style={{ color: GOLD }}
            />
          )}
          <span
            className="self-start text-[10px] uppercase tracking-[0.25em] px-3 py-1 text-white"
            style={{ backgroundColor: LILAC, borderRadius: "var(--caileve-radius-pill)" }}
          >
            Founding Member
          </span>
          <h2
            className="text-3xl mt-4"
            style={{ fontFamily: SERIF, color: INK }}
          >
            Premium
          </h2>
          {isAnnual ? (
            <div className="mt-3">
              <span
                className="text-3xl"
                style={{ fontFamily: SERIF, color: INK }}
              >
                $55.99/yr
              </span>
              <p
                className="mt-1 italic text-sm"
                style={{ fontFamily: SERIF, color: LILAC }}
              >
                just $4.67/month
              </p>
              <p
                className="mt-3 pt-3 uppercase"
                style={{
                  color: GOLD,
                  fontSize: 10,
                  letterSpacing: "0.25em",
                  borderTop: `1px solid ${GOLD}33`,
                }}
              >
                Founding rate — locks in permanently
              </p>
            </div>
          ) : (
            <>
              <div className="mt-3 flex items-baseline gap-2">
                <span
                  className="text-3xl"
                  style={{ fontFamily: SERIF, color: INK }}
                >
                  $6.99/month
                </span>
                <span
                  className="text-lg line-through"
                  style={{ fontFamily: SERIF, color: MUTED }}
                >
                  $9.99
                </span>
              </div>
              <p className="text-sm mt-1" style={{ color: MUTED }}>
                $55.99/year
              </p>
            </>
          )}
          <ul
            className="mt-6 space-y-2 text-sm leading-relaxed flex-1"
            style={{ color: INK }}
          >
            <li>Unlimited style judgment. Cailève with you whenever you shop or dress.</li>
            <li>Unlimited Cailève Eye scans</li>
            <li>All Free features, no limits</li>
            <li>Your Style Intelligence — growing with every piece</li>
            <li>Shareable looks — share your looks as beautiful cards</li>
          </ul>
          {isFree && (
            <p
              className="mt-4 italic text-sm"
              style={{ fontFamily: SERIF, color: MUTED }}
            >
              Upgrade to Premium to unlock this feature.
            </p>
          )}
          {isPremium ? (
            <p
              className="mt-8 text-center"
              style={{
                fontFamily: SERIF,
                fontStyle: "italic",
                fontSize: 13,
                color: "var(--caileve-gold-text)",
                letterSpacing: "0.05em",
              }}
            >
              ✦ Your current plan
            </p>
          ) : (
            <button
              type="button"
              onClick={() => handleUpgrade("premium")}
              disabled={loadingTier !== null}
              className="mt-8 w-full py-3 text-xs uppercase tracking-[0.25em] text-white hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-wait"
              style={{ backgroundColor: LILAC }}
            >
              {loadingTier === "premium" ? "Redirecting…" : "Upgrade to Premium"}
            </button>
          )}
          {isPremium && hasStripeSub && (
            <button
              type="button"
              onClick={openPortal}
              disabled={portalBusy}
              className="mt-3 italic underline self-center disabled:opacity-60"
              style={{
                fontSize: "13px",
                color: MUTED,
                fontFamily: SERIF,
              }}
            >
              {portalBusy ? "Opening…" : "Manage or cancel your subscription"}
            </button>
          )}
          {isPremium && hasStripeSub && (
            <p
              className="mt-2 text-center text-xs italic"
              style={{
                color: "var(--caileve-muted)",
                fontFamily: "'Cormorant Garamond', Georgia, serif",
              }}
            >
              To switch to a lower plan or return to free, open the portal and choose Update subscription or Cancel subscription.
            </p>
          )}
        </article>

        {/* Atelier */}
        <article
          className="p-8 flex flex-col relative"
          style={{
            backgroundColor: INK,
            borderTop: isAtelier ? `1px solid ${GOLD}` : "none",
          }}
        >
          {isAtelier && (
            <Check
              size={14}
              className="absolute top-3 right-3"
              style={{ color: GOLD }}
            />
          )}
          <span
            className="self-start text-[10px] uppercase tracking-[0.25em] px-3 py-1"
            style={{
              backgroundColor: GOLD,
              color: INK,
              borderRadius: "var(--caileve-radius-pill)",
            }}
          >
            Founding Member
          </span>
          <h2
            className="text-3xl mt-4"
            style={{ fontFamily: SERIF, color: BG }}
          >
            Atelier
          </h2>
          {isAnnual ? (
            <div className="mt-3">
              <span
                className="text-3xl"
                style={{ fontFamily: SERIF, color: GOLD }}
              >
                $119.99/yr
              </span>
              <p
                className="mt-1 italic text-sm"
                style={{ fontFamily: SERIF, color: LILAC }}
              >
                just $10.00/month
              </p>
              <p
                className="mt-3 pt-3 uppercase"
                style={{
                  color: GOLD,
                  fontSize: 10,
                  letterSpacing: "0.25em",
                  borderTop: `1px solid ${GOLD}33`,
                }}
              >
                Founding rate — locks in permanently
              </p>
            </div>
          ) : (
            <>
              <div className="mt-3 flex items-baseline gap-2">
                <span
                  className="text-3xl"
                  style={{ fontFamily: SERIF, color: GOLD }}
                >
                  $14.99/month
                </span>
                <span
                  className="text-lg line-through"
                  style={{ fontFamily: SERIF, color: MUTED }}
                >
                  $19.99
                </span>
              </div>
              <p className="text-sm mt-1" style={{ color: MUTED }}>
                $119.99/year
              </p>
            </>
          )}
          <ul
            className="mt-6 space-y-2 text-sm leading-relaxed flex-1"
            style={{ color: BG }}
          >
            <li>All Premium features</li>
            <li>Circle Founder status — first access when Private Circles launches</li>
            <li>Priority access to every new Cailève feature</li>
          </ul>
          {!isAtelier && (
            <p
              className="mt-4 italic text-sm"
              style={{ fontFamily: SERIF, color: MUTED }}
            >
              Available exclusively to Atelier members.
            </p>
          )}
          {isAtelier ? (
            <button
              type="button"
              disabled
              className="mt-8 w-full py-3 text-xs uppercase tracking-[0.25em] cursor-not-allowed"
              style={{ backgroundColor: MUTED, color: INK }}
            >
              Current Plan
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleUpgrade("atelier")}
              disabled={loadingTier !== null}
              className="mt-8 w-full py-3 text-xs uppercase tracking-[0.25em] hover:opacity-90 transition-opacity bg-transparent disabled:opacity-60 disabled:cursor-wait"
              style={{ border: `1px solid ${GOLD}`, color: GOLD }}
            >
              {loadingTier === "atelier" ? "Redirecting…" : "Upgrade to Atelier"}
            </button>
          )}
          {isAtelier && hasStripeSub && (
            <button
              type="button"
              onClick={openPortal}
              disabled={portalBusy}
              className="mt-3 italic underline self-center disabled:opacity-60"
              style={{
                fontSize: "13px",
                color: MUTED,
                fontFamily: SERIF,
              }}
            >
              {portalBusy ? "Opening…" : "Manage or cancel your subscription"}
            </button>
          )}
          {isAtelier && hasStripeSub && (
            <p
              className="mt-2 text-center text-xs italic"
              style={{
                color: "var(--caileve-muted)",
                fontFamily: "'Cormorant Garamond', Georgia, serif",
              }}
            >
              To switch to a lower plan or return to free, open the portal and choose Update subscription or Cancel subscription.
            </p>
          )}
        </article>
      </div>

      {isAnnual && (
        <p
          className="text-center mt-6"
          style={{ color: MUTED, fontSize: 11 }}
        >
          Billed once annually. Cancel anytime.
        </p>
      )}

      <p
        className="max-w-xl mx-auto px-6 mt-12 text-center italic"
        style={{ fontFamily: SERIF, color: MUTED }}
      >
        The women who wear their collections best know exactly what they own — and exactly which pieces belong with them.
      </p>
      <p
        className="max-w-2xl mx-auto px-6 mt-6 text-center italic text-sm"
        style={{ fontFamily: SERIF, color: MUTED }}
      >
        Founding Member pricing is available to the first 100 subscribers and
        is locked in forever.
      </p>

      <Sheet open={comingSoonOpen} onOpenChange={setComingSoonOpen}>
        <SheetContent
          side="bottom"
          style={{ backgroundColor: BG, borderTop: `1px solid ${GOLD}` }}
          className="px-8 pt-10 pb-10"
        >
          <SheetTitle
            className="text-2xl md:text-3xl text-center"
            style={{ fontFamily: SERIF, color: INK }}
          >
            Coming Soon
          </SheetTitle>
          <p
            className="mt-4 text-center text-base leading-relaxed max-w-md mx-auto"
            style={{ color: MUTED }}
          >
            Subscription payments are launching soon. You'll be notified the
            moment they're available.
          </p>
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setComingSoonOpen(false)}
              className="px-10 py-3 text-xs uppercase tracking-[0.25em] text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: LILAC }}
            >
              Got it
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}